import * as React from "react";
import { useEffect } from "react";
import apiClient from "@/api-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Link } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import { EyeIcon } from "@phosphor-icons/react/dist/ssr/Eye";
import { EyeSlashIcon } from "@phosphor-icons/react/dist/ssr/EyeSlash";
import { useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { z as zod } from "zod";

import { paths } from "@/paths";
import { logger } from "@/lib/default-logger";
import { IN_SMB } from "@/lib/platform";
import { CenteredLayout } from "@/components/auth/centered-layout";
import { useAnimation } from "@/components/core/animation-provider";
import { useAuth } from "@/components/core/auth-provider";
import { RouterLink } from "@/components/core/link";
import { DynamicLogo } from "@/components/core/logo";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";

const schema = zod.object({
	email: zod.string().min(1, { message: "Email is required" }).email(),
	password: zod.string().min(1, { message: "Password is required" }),
});

const codeSchema = zod.object({
	code: zod.string().min(1, { message: "Code is required" }),
});

type Values = zod.infer<typeof schema>;
type CodeValues = zod.infer<typeof codeSchema>;

export function SignIn(): React.JSX.Element {
	const navigate = useNavigate();
	const { getMe } = useAuth();
	// Set by api-client.ts when it signs the user out, e.g. `refresh_401`.
	const [searchParams] = useSearchParams();
	const wasSignedOut = searchParams.has("reason");
	const { setIsAnimated } = useAnimation();

	const queryClient = useQueryClient();

	const [showPassword, setShowPassword] = React.useState<boolean>();

	const [is2FA, setIs2FA] = React.useState<boolean>(false);
	const [isGoogle2FA, setIsGoogle2FA] = React.useState<boolean>(false);
	const [challangeId, setChallangeId] = React.useState<string>("");
	const [nextAttemptAt, setNextAttemptAt] = React.useState<number | null>(null);

	const [isResending, setIsResending] = React.useState<boolean>(false);
	const [canResend, setCanResend] = React.useState<boolean>(true);
	const [timeRemaining, setTimeRemaining] = React.useState<number>(0);
	const [isSendingEmailCode, setIsSendingEmailCode] = React.useState<boolean>(false);

	const [isVerifyCodeBlocked, setIsVerifyCodeBlocked] = React.useState<boolean>(false);
	const [nextVerifyAttemptAt, setNextVerifyAttemptAt] = React.useState<number | null>(null);
	const [verifyTimeRemaining, setVerifyTimeRemaining] = React.useState<number>(0);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<Values>({ defaultValues: { email: "", password: "" }, resolver: zodResolver(schema) });

	const {
		control: codeControl,
		handleSubmit: handleCodeSubmit,
		formState: { errors: codeErrors, isSubmitting: isCodeSubmitting },
	} = useForm<CodeValues>({ defaultValues: { code: "" }, resolver: zodResolver(codeSchema) });

	React.useEffect(() => {
		if (!nextAttemptAt) {
			setCanResend(true);
			setTimeRemaining(0);
			return;
		}

		const updateTimer = () => {
			const now = Date.now();
			const nextAttemptMs = nextAttemptAt * 1000;
			const remaining = Math.max(0, Math.ceil((nextAttemptMs - now) / 1000));
			setTimeRemaining(remaining);
			const canAttempt = now >= nextAttemptMs;
			setCanResend(canAttempt);
		};

		updateTimer();
		const interval = setInterval(updateTimer, 1000);

		return () => clearInterval(interval);
	}, [nextAttemptAt]);

	React.useEffect(() => {
		if (!nextVerifyAttemptAt) {
			setIsVerifyCodeBlocked(false);
			setVerifyTimeRemaining(0);
			return;
		}

		const updateVerifyTimer = () => {
			const now = Date.now();
			const nextAttemptMs = nextVerifyAttemptAt * 1000;
			const remaining = Math.max(0, Math.ceil((nextAttemptMs - now) / 1000));
			setVerifyTimeRemaining(remaining);
			const canAttempt = now >= nextAttemptMs;
			if (canAttempt) {
				setIsVerifyCodeBlocked(false);
			}
		};

		updateVerifyTimer();
		const interval = setInterval(updateVerifyTimer, 1000);

		return () => clearInterval(interval);
	}, [nextVerifyAttemptAt]);

	useEffect(() => {
		setIsAnimated(false);
		queryClient.clear();
		localStorage.removeItem("token");
	}, [queryClient, setIsAnimated]);

	const onSubmit = async (data: Values) => {
		if (!is2FA) {
			try {
				const response = await apiClient.post("/auth/login", data);
				setChallangeId(response.data.id);

				// If nextAttemptAt is not provided, it means Google 2FA is enabled
				if (response.data.nextAttemptAt !== undefined && response.data.nextAttemptAt !== null) {
					setNextAttemptAt(Number(response.data.nextAttemptAt));
					setIsGoogle2FA(false);
					setCanResend(false);
					toast.success("2FA code sent to your email");
				} else {
					setNextAttemptAt(null);
					setIsGoogle2FA(true);
					toast.success("Enter the code from your authenticator app");
				}

				setIs2FA(true);
			} catch (error: any) {
				logger.error(error);
				toast.error(error?.response?.data?.statusCode === 429 ? "Too many attempts. Please wait before trying again" : "Invalid credentials");
			}
			return;
		}
	};

	const onCodeSubmit = async (data: CodeValues) => {
		try {
			const response = await apiClient.post(
				"/auth/verify",
				{
					challangeId: challangeId,
					code: data.code,
				},
				{
					withCredentials: true,
				}
			);

			localStorage.setItem("token", response.data.access_token);

			setNextVerifyAttemptAt(null);
			setIsVerifyCodeBlocked(false);

			toast.success("Sign in successful");

			// The business app routes by the profile: its owner goes straight in, anyone else to the dashboard.
			if (IN_SMB) {
				await getMe(true);
			} else {
				navigate("/accounts");
				await getMe();
			}
		} catch (error: any) {
			logger.error(error);

			const retryAfterHeader = error?.response?.headers?.["retry-after"];

			if (retryAfterHeader && error?.response?.status === 429) {
				const retryAfterSeconds = Number(retryAfterHeader);

				if (!Number.isNaN(retryAfterSeconds) && retryAfterSeconds > 0) {
					const nextAttemptTimestamp = Math.floor(Date.now() / 1000) + retryAfterSeconds;
					setNextVerifyAttemptAt(nextAttemptTimestamp);
					setIsVerifyCodeBlocked(true);

					toast.error("Too many attempts. Please wait before trying again");
				} else {
					toast.error(error?.response?.data?.message || "Too many attempts. Please try again later");
				}
			} else {
				toast.error("Invalid code");
			}
		}
	};

	const handleResendCode = async () => {
		if (!canResend || isResending) return;

		setIsResending(true);
		try {
			const response = await apiClient.post("/auth/resend-code", {
				challangeId,
			});
			setChallangeId(response.data.id);
			setNextAttemptAt(Number(response.data.nextAttemptAt));
			toast.success("2FA code resent to your email");
		} catch (error) {
			logger.error(error);
			toast.error("Failed to resend code");
		} finally {
			setIsResending(false);
		}
	};

	const handleSendEmailCode = async () => {
		if (isSendingEmailCode) return;

		setIsSendingEmailCode(true);
		try {
			const response = await apiClient.post("/auth/resend-code", {
				challangeId,
			});
			setChallangeId(response.data.id);
			setNextAttemptAt(Number(response.data.nextAttemptAt));
			setIsGoogle2FA(false);
			setCanResend(false);
			toast.success("2FA code sent to your email");
		} catch (error) {
			logger.error(error);
			toast.error("Failed to send code to email");
		} finally {
			setIsSendingEmailCode(false);
		}
	};

	const isResendCodeButtonDisabled = !canResend || isResending || isVerifyCodeBlocked;
	const shouldShowResendCodeText = !canResend && nextAttemptAt && timeRemaining > 0 && !isVerifyCodeBlocked;
	const isVerifyCodeButtonDisabled = isVerifyCodeBlocked && verifyTimeRemaining > 0;

	return (
		<React.Fragment>
			<CenteredLayout>
				<Stack spacing={4}>
					<div>
						<Box
							component={RouterLink}
							href={paths.home}
							sx={{ display: "flex", fontSize: 0, justifyContent: "center" }}
						>
							<DynamicLogo colorDark="light" colorLight="dark" height={42} width={222} />
						</Box>
					</div>
					{is2FA ? (
						<Card key={is2FA ? "2fa" : "sign-in"}>
							<CardHeader
								title="Two-Factor Authentication"
								subheader={
									isGoogle2FA ? "Enter the code from your authenticator app" : "Enter the code sent to your email"
								}
							/>
							<CardContent>
								<form onSubmit={handleCodeSubmit(onCodeSubmit)}>
									<Stack spacing={2}>
										<Stack spacing={2}>
											<Controller
												control={codeControl}
												name="code"
												render={({ field }) => (
													<FormControl error={Boolean(codeErrors.code)}>
														<InputLabel>Verification Code</InputLabel>
														<OutlinedInput {...field} type="text" placeholder="Enter code" />
														{codeErrors.code ? <CustomFormHelperError text={codeErrors.code.message || ""} /> : null}
													</FormControl>
												)}
											/>
											<Button
												type="submit"
												variant="contained"
												loading={isCodeSubmitting}
												disabled={isVerifyCodeButtonDisabled}
											>
												Verify Code{" "}
												{isVerifyCodeBlocked && verifyTimeRemaining > 0 && `(Available in ${verifyTimeRemaining}s)`}
											</Button>
										</Stack>
										{!isGoogle2FA && (
											<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
												<Button
													variant="outlined"
													onClick={handleResendCode}
													disabled={isResendCodeButtonDisabled}
													loading={isResending}
													size="small"
												>
													Resend Code
												</Button>
												{shouldShowResendCodeText && (
													<Box sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
														(Resend available in {timeRemaining}s)
													</Box>
												)}
											</Stack>
										)}
										{isGoogle2FA && (
											<Button
												variant="outlined"
												onClick={handleSendEmailCode}
												disabled={isSendingEmailCode}
												loading={isSendingEmailCode}
												size="small"
												fullWidth
											>
												Send code to email
											</Button>
										)}
									</Stack>
								</form>
							</CardContent>
						</Card>
					) : (
						<Card key={is2FA ? "2fa" : "sign-in"}>
							<CardHeader title="Sign in" subheader="Enter your email and password to sign in" />
							{wasSignedOut && (
								<Alert severity="info" sx={{ mx: 2 }}>
									Your session has ended. Please sign in again.
								</Alert>
							)}
							<CardContent>
								<form onSubmit={handleSubmit(onSubmit)}>
									<Stack spacing={2}>
										<Stack spacing={2}>
											<Controller
												control={control}
												name="email"
												render={({ field }) => (
													<FormControl error={Boolean(errors.email)}>
														<InputLabel>Email address</InputLabel>
														<OutlinedInput {...field} type="email" />
														{errors.email ? <CustomFormHelperError text={errors.email.message || ""} /> : null}
													</FormControl>
												)}
											/>
											<Controller
												control={control}
												name="password"
												render={({ field }) => (
													<FormControl error={Boolean(errors.password)}>
														<InputLabel>Password</InputLabel>
														<OutlinedInput
															{...field}
															endAdornment={
																showPassword ? (
																	<EyeIcon
																		cursor="pointer"
																		fontSize="var(--icon-fontSize-md)"
																		onClick={(): void => {
																			setShowPassword(false);
																		}}
																	/>
																) : (
																	<EyeSlashIcon
																		cursor="pointer"
																		fontSize="var(--icon-fontSize-md)"
																		onClick={(): void => {
																			setShowPassword(true);
																		}}
																	/>
																)
															}
															type={showPassword ? "text" : "password"}
														/>
														{errors.password ? <CustomFormHelperError text={errors.password.message || ""} /> : null}
													</FormControl>
												)}
											/>
											<Button type="submit" variant="contained" loading={isSubmitting}>
												Sign in
											</Button>
										</Stack>
										<Stack direction="row" sx={{ justifyContent: "space-between" }}>
											<Link component={RouterLink} href="/forgot-password" variant="subtitle2">
												Forgot password?
											</Link>
											{IN_SMB ? (
												<Link component={RouterLink} href="/sign-up" variant="subtitle2">
													Create a business account
												</Link>
											) : null}
										</Stack>
									</Stack>
								</form>
							</CardContent>
						</Card>
					)}
				</Stack>
			</CenteredLayout>
		</React.Fragment>
	);
}
